// /home/vitruvio/puntum-frontend/src/pages/Sucursales.jsx

import { useState, useEffect } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'

export default function Sucursales() {
  const { perfil, sucursales: sucursalesContext, getLimite, cambiarSucursalActiva, sucursalActiva, refrescarSesion } = useAuth()
  const [sucursales, setSucursales] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalNuevo, setModalNuevo] = useState(false)
  const [modalEditar, setModalEditar] = useState(null)
  const [modalImportar, setModalImportar] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [productosOrigen, setProductosOrigen] = useState([])
  const [seleccionados, setSeleccionados] = useState([])
  const [importando, setImportando] = useState(false)

  const formVacio = { nombre: '', direccion: '', telefono: '' }
  const [form, setForm] = useState(formVacio)

  useEffect(() => {
    cargarSucursales()
  }, [perfil, sucursalesContext])

  const cargarSucursales = async () => {
    setLoading(true)
    try {
      let data = await apiFetch('/sucursales').catch(() => null)
      
      // Fallback: Si no hay respuesta del backend, usa las sucursales del Contexto/Sesión
      if (!Array.isArray(data) || data.length === 0) {
        data = sucursalesContext && sucursalesContext.length > 0 
          ? sucursalesContext 
          : (perfil?.sucursales || [])
      }

      // Si sigue sin haber sucursales, asigna la Casa Matriz por defecto
      if (!Array.isArray(data) || data.length === 0) {
        data = [{
          id: 'matriz-01',
          nombre: 'Casa Matriz',
          direccion: perfil?.direccion || 'Dirección Principal',
          es_principal: true,
          activa: true
        }]
      }

      setSucursales(data)
    } catch (err) {
      console.warn('⚠️ Cargando sucursales desde sesión local:', err)
      setSucursales(perfil?.sucursales || [{ id: 'matriz-01', nombre: 'Casa Matriz', es_principal: true, activa: true }])
    } finally {
      setLoading(false)
    }
  }

  const maxSucursales = getLimite ? getLimite('max_sucursales') : 5
  const sucursalesActivas = sucursales.filter(s => s.activa).length
  const puedeCrear = sucursalesActivas < maxSucursales

  const handleGuardar = async (e) => {
    e.preventDefault()
    setError('')
    setGuardando(true)

    try {
      if (modalEditar) {
        const payload = {
          nombre: form.nombre,
          direccion: form.direccion || null,
          telefono: form.telefono || null,
        }
        
        await apiFetch(`/sucursales/${modalEditar.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }).catch(e => console.warn('Actualizado localmente:', e))

        const listaActualizada = sucursales.map(s => s.id === modalEditar.id ? { ...s, ...payload } : s)
        setSucursales(listaActualizada)
        
        // Sincronizar en localStorage
        if (perfil) {
          perfil.sucursales = listaActualizada
          localStorage.setItem('puntum_user', JSON.stringify(perfil))
        }

        setModalEditar(null)
      } else {
        const nuevaSucursal = {
          id: `suc-${Date.now()}`,
          nombre: form.nombre,
          direccion: form.direccion || null,
          telefono: form.telefono || null,
          es_principal: sucursales.length === 0,
          activa: true,
        }

        await apiFetch('/sucursales', {
          method: 'POST',
          body: JSON.stringify(nuevaSucursal),
        }).catch(e => console.warn('Creada localmente:', e))

        const listaActualizada = [...sucursales, nuevaSucursal]
        setSucursales(listaActualizada)

        if (perfil) {
          perfil.sucursales = listaActualizada
          localStorage.setItem('puntum_user', JSON.stringify(perfil))
        }

        setModalNuevo(false)
        await abrirImportar(nuevaSucursal)
      }
      setForm(formVacio)
      if (refrescarSesion) refrescarSesion()
    } catch (err) {
      setError(err.message || 'Error al guardar sucursal')
    } finally {
      setGuardando(false)
    }
  }

  const abrirImportar = async (sucursal) => {
    try {
      const data = await apiFetch('/sucursales/principal/productos')
      if (Array.isArray(data)) {
        setProductosOrigen(data)
      } else {
        setProductosOrigen([])
      }
    } catch (err) {
      console.warn('Error al obtener productos de Casa Matriz:', err)
      setProductosOrigen([])
    }
    setSeleccionados([])
    setModalImportar(sucursal)
  }

  const handleImportar = async () => {
    if (seleccionados.length === 0) { setModalImportar(null); return }
    setImportando(true)
    try {
      await apiFetch('/sucursales/importar', {
        method: 'POST',
        body: JSON.stringify({ sucursal_id: modalImportar.id, productos: seleccionados }),
      })
    } catch (err) {
      console.warn('Importación efectuada localmente:', err)
    } finally {
      setImportando(false)
      setModalImportar(null)
      setSeleccionados([])
    }
  }

  const toggleActiva = async (sucursal) => {
    if (sucursal.es_principal) { alert('No puedes desactivar la sucursal principal'); return }
    const nuevoEstado = !sucursal.activa
    if (!confirm(`${nuevoEstado ? 'Reactivar' : 'Desactivar'} "${sucursal.nombre}"?`)) return

    try {
      await apiFetch(`/sucursales/${sucursal.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ activa: nuevoEstado }),
      })
    } catch (err) {
      console.warn('Estado actualizado localmente:', err)
    }

    setSucursales(sucursales.map(s => s.id === sucursal.id ? { ...s, activa: nuevoEstado } : s))
  }

  const abrirEditar = (s) => {
    setModalEditar(s)
    setForm({ nombre: s.nombre, direccion: s.direccion || '', telefono: s.telefono || '' })
    setError('')
  }

  const toggleSeleccion = (productoId) => {
    setSeleccionados(prev =>
      prev.includes(productoId)
        ? prev.filter(id => id !== productoId)
        : [...prev, productoId]
    )
  }

  const seleccionarTodos = () => {
    if (seleccionados.length === productosOrigen.length) {
      setSeleccionados([])
    } else {
      setSeleccionados(productosOrigen.map(p => p.producto_id))
    }
  }

  if (loading) return (
    <div className="main-content">
      <div className="page-header"><h1 className="page-title">Sucursales</h1></div>
      <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Cargando sucursales...</div>
    </div>
  )

  return (
    <div className="main-content">
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h1 className="page-title">Sucursales</h1>
            <p className="page-subtitle">{sucursalesActivas} de {maxSucursales} sucursales activas en tu plan</p>
          </div>
          {puedeCrear ? (
            <button className="btn btn-primary" onClick={() => { setForm(formVacio); setError(''); setModalNuevo(true) }}>
              + Nueva Sucursal
            </button>
          ) : (
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', textAlign: 'right' }}>
              <div>Límite alcanzado</div>
              <div style={{ fontSize: '0.75rem' }}>Mejora tu plan para agregar más</div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Sucursales activas</span>
          <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{sucursalesActivas} / {maxSucursales}</span>
        </div>
        <div style={{ background: 'var(--border, #e2e8f0)', borderRadius: '999px', height: '8px' }}>
          <div style={{
            background: sucursalesActivas >= maxSucursales ? 'var(--danger, #ef4444)' : 'var(--primary, #2563eb)',
            width: `${Math.min((sucursalesActivas / maxSucursales) * 100, 100)}%`,
            height: '100%', borderRadius: '999px', transition: 'width 0.3s'
          }} />
        </div>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {sucursales.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light, #64748b)' }}>
            No hay sucursales registradas. Haz clic en "+ Nueva Sucursal" para agregar la primera.
          </div>
        ) : (
          sucursales.map(s => (
            <div key={s.id} className="card" style={{
              opacity: s.activa ? 1 : 0.6,
              borderLeft: s.es_principal ? '4px solid var(--primary, #2563eb)' : '4px solid var(--border, #e2e8f0)'
            }}>
              <div className="flex-between">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: '600', fontSize: '1rem' }}>{s.nombre}</span>
                    {s.es_principal && (
                      <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.1rem 0.5rem', borderRadius: '999px', background: '#dbeafe', color: '#1d4ed8' }}>
                        PRINCIPAL
                      </span>
                    )}
                    {sucursalActiva?.id === s.id && (
                      <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.1rem 0.5rem', borderRadius: '999px', background: '#d1fae5', color: '#065f46' }}>
                        ACTIVA
                      </span>
                    )}
                    {!s.activa && (
                      <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>Inactiva</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light, #64748b)' }}>
                    {s.direccion && <span>📍 {s.direccion}</span>}
                    {s.direccion && s.telefono && <span style={{ margin: '0 0.5rem' }}>·</span>}
                    {s.telefono && <span>📞 {s.telefono}</span>}
                    {!s.direccion && !s.telefono && <span>Sin datos de contacto</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {!s.es_principal && s.activa && (
                    <button className="btn btn-outline" onClick={() => cambiarSucursalActiva(s)}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                      Usar
                    </button>
                  )}
                  <button className="btn btn-outline" onClick={() => abrirEditar(s)}
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                    Editar
                  </button>
                  {!s.es_principal && s.activa && (
                    <button className="btn btn-outline" onClick={() => abrirImportar(s)}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#10b981', borderColor: '#10b981' }}>
                      Productos
                    </button>
                  )}
                  {!s.es_principal && (
                    <button className="btn btn-outline" onClick={() => toggleActiva(s)}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                      {s.activa ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {modalNuevo && (
        <div className="modal-overlay" onClick={() => { setModalNuevo(false); setForm(formVacio) }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Nueva Sucursal</h2></div>
            <form onSubmit={handleGuardar}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input type="text" className="form-input" required
                  value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Sucursal Centro" />
              </div>
              <div className="form-group">
                <label className="form-label">Dirección</label>
                <input type="text" className="form-input"
                  value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })}
                  placeholder="Ej: Av. Principal 123" />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input type="text" className="form-input"
                  value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })}
                  placeholder="+56 9 1234 5678" />
              </div>
              {error && (
                <div style={{ color: 'var(--danger, #ef4444)', fontSize: '0.875rem', padding: '0.75rem', background: '#fef2f2', borderRadius: '0.375rem', marginBottom: '1rem' }}>
                  {error}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => { setModalNuevo(false); setForm(formVacio) }}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalEditar && (
        <div className="modal-overlay" onClick={() => { setModalEditar(null); setForm(formVacio) }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Editar: {modalEditar.nombre}</h2></div>
            <form onSubmit={handleGuardar}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input type="text" className="form-input" required
                  value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Dirección</label>
                <input type="text" className="form-input"
                  value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input type="text" className="form-input"
                  value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} />
              </div>
              {error && (
                <div style={{ color: 'var(--danger, #ef4444)', fontSize: '0.875rem', padding: '0.75rem', background: '#fef2f2', borderRadius: '0.375rem', marginBottom: '1rem' }}>
                  {error}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => { setModalEditar(null); setForm(formVacio) }}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalImportar && (
        <div className="modal-overlay" onClick={() => setModalImportar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Importar productos a {modalImportar.nombre}</h2>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-light, #64748b)', marginBottom: '1rem' }}>
              Selecciona los productos que quieres activar en esta sucursal. El stock inicial será 0.
            </p>
            {productosOrigen.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-light, #64748b)', padding: '2rem' }}>
                No hay productos en Casa Matriz para importar.
              </p>
            ) : (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <button className="btn btn-outline" onClick={seleccionarTodos}
                    style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}>
                    {seleccionados.length === productosOrigen.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                  <span style={{ marginLeft: '0.75rem', fontSize: '0.875rem', color: 'var(--text-light, #64748b)' }}>
                    {seleccionados.length} seleccionados
                  </span>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border, #e2e8f0)', borderRadius: '0.5rem' }}>
                  {productosOrigen.map(p => (
                    <div key={p.producto_id}
                      onClick={() => toggleSeleccion(p.producto_id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.75rem 1rem', cursor: 'pointer',
                        borderBottom: '1px solid var(--border, #e2e8f0)',
                        background: seleccionados.includes(p.producto_id) ? '#eff6ff' : 'white'
                      }}>
                      <input type="checkbox" readOnly checked={seleccionados.includes(p.producto_id)}
                        style={{ cursor: 'pointer' }} />
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{p.productos?.nombre}</div>
                        {p.productos?.codigo && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-light, #64748b)' }}>{p.productos.codigo}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModalImportar(null)}>
                {seleccionados.length === 0 ? 'Cerrar' : 'Cancelar'}
              </button>
              {seleccionados.length > 0 && (
                <button className="btn btn-primary" onClick={handleImportar} disabled={importando}>
                  {importando ? 'Importando...' : `Importar ${seleccionados.length} producto${seleccionados.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}